
import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Loader2, AlertCircle, CheckCircle, Info } from "lucide-react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import SearchSection from "@/components/SearchSection";
import TrendingCoinCard from "@/components/TrendingCoinCard";
import { KingPreview } from "@/components/KingOfTheHill/KingPreview";
import { useQuickSwapPools } from "@/hooks/useQuickSwapPools";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCoins, fallbackCoins } from "@/hooks/useCoins";

const Index = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { 
    data: poolsData, 
    isLoading: isPoolsLoading, 
    error: poolsError,
    lastErrorMessage,
    isApiDown
  } = useQuickSwapPools();
  const { data: coinsData, isLoading: isCoinsLoading } = useCoins(100);
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const matchingCoins = (coinsData?.coins || fallbackCoins)
    .filter((coin) => !normalizedSearch || `${coin.name} ${coin.ticker} ${coin.coinAddress || ""} ${coin.creator}`.toLowerCase().includes(normalizedSearch));
  const trendingCoins = matchingCoins
    .slice(0, 3).map((coin, index) => ({ ...coin, isHot: index < 2 }));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <HeroSection />
        <SearchSection searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        {coinsData?.isFallback && <div className="max-w-2xl mx-auto mb-8 rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">Live token data is unavailable, so example communities are being shown.</div>}
        <KingPreview />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center mb-6">
            <TrendingUp className="h-5 w-5 text-primary mr-2" />
            <h2 className="text-xl font-semibold">Trending Communities</h2>
          </div>

          {isCoinsLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading live tokens…</div>
          ) : matchingCoins.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">No tokens match “{searchQuery}”.</div>
          ) : <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {trendingCoins.map((coin) => (
              <TrendingCoinCard key={coin.id} coin={coin} />
            ))}
          </div>}

          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Top QuickSwap Pools</h2>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center text-sm text-muted-foreground">
                    <span className="mr-1">Pool Status</span>
                    {poolsError ? (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {poolsError 
                      ? `Error: ${lastErrorMessage || 'Connection issue'}`
                      : 'QuickSwap pools are connected and working'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {isPoolsLoading ? (
                <div className="col-span-full flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : isApiDown ? (
                <div className="col-span-full p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Info className="h-5 w-5" />
                    <p className="font-medium">QuickSwap API Currently Unavailable</p>
                  </div>
                  <p className="text-sm">
                    We're displaying placeholder data until the connection is restored.
                  </p>
                </div>
              ) : poolsError ? (
                <div className="col-span-full p-4 rounded-lg bg-destructive/10 text-destructive text-center">
                  <p>Pool data coming soon. Please try again later.</p>
                  <p className="text-xs mt-2">
                    Your token is still created successfully. The pools will be available once connected.
                  </p>
                </div>
              ) : !poolsData?.pools?.length ? (
                <div className="col-span-full p-4 rounded-lg bg-muted text-center">
                  No pools available at the moment.
                </div>
              ) : (
                poolsData.pools.map((pool) => (
                  <div key={pool.id} className="p-4 rounded-lg bg-card border hover:shadow-md transition-shadow">
                    <h3 className="font-medium">
                      {pool.token0.symbol}/{pool.token1.symbol}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      TVL: ${Number(pool.totalValueLockedUSD).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Volume: ${Number(pool.volumeUSD).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
            
            {!poolsError && poolsData?.pools?.length > 0 && (
              <div className="mt-4 text-sm text-muted-foreground">
                <p>
                  Note: New tokens may take up to 24 hours to appear in QuickSwap pools after liquidity is added.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Index;
